import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Paper,
    Chip,
    InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import CustomDataGrid from "../../Components/CustomDataGrid";
import { columns } from "./config";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { WhatsApp } from "@mui/icons-material";
import { toast } from "react-toastify";
import AddOtherPartyDialog from "../../Components/Dialogs/AddOtherPartyDialog";
import EditOtherPartyDialog from "../../Components/Dialogs/EditOtherPartyDialog";
import { useLang } from "../../context/LanguageContext";

const CustomerList = () => {
    const navigate = useNavigate();
    const authHeader = useAuthHeader();
    const { t } = useLang();

    // State variables
    const [otherParties, setOtherParties] = useState([]);
    const [isRefresh, setIsRefresh] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false); // For Add Dialog
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false); // For Edit Dialog
    const [selectedRowId, setSelectedRowId] = useState(null); // Row ID for Edit/Delete
    const [selectedEditId, setSelectedEditId] = useState(null); // ID to Edit

    // Fetch Customers
    const fetchCustomers = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/otherparty`,
                {
                    method: "GET",
                    headers: {
                        Authorization: authHeader,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.ok) {
                const responseData = await response.json();
                setOtherParties(responseData.otherParties);
            } else {
                throw new Error("Failed to fetch customers");
            }
        } catch (err) {
            toast.error(err.message || "Error fetching customers.");
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, [authHeader, isRefresh]);

    // Delete Handler
    const handleConfirmDelete = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/otherparty/${selectedRowId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: authHeader,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.ok) {
                toast.success("Other party deleted successfully.");
                setIsRefresh(!isRefresh);
            } else {
                throw new Error("Other party has sales associated with it.");
            }
        } catch (err) {
            toast.error(err.message || "Error deleting customer.");
        } finally {
            setOpenDialog(false);
        }
    };

    // View Details
    const handleViewDetails = (id) => {
        navigate(`/CustomerDetails/${id}`);
    };

    // Actions for Data Grid
    const actions = [
        {
            title: "View Details",
            color: "info",
            handler: handleViewDetails,
            parameters: ["id"],
            icon: <PreviewIcon />,
        },
        {
            title: "Edit",
            color: "primary",
            handler: (id) => {
                setSelectedEditId(id);
                setIsEditDialogOpen(true);
            },
            parameters: ["id"],
            icon: <EditIcon />, // Correct icon for Edit action
        },
        {
            title: "Delete",
            color: "error",
            handler: (id) => {
                setSelectedRowId(id);
                setOpenDialog(true);
            },
            parameters: ["id"],
            icon: <DeleteIcon />,
        },
        {
            title: "Open Chat",
            color: "success",
            icon: <WhatsApp />,
            parameters: ["phone"],
            handler: (row) => {
                window.open(`https://wa.me/${row}`, "_blank");
            },
        },
    ];

    // Filter other parties based on search term
    const filteredOtherParties = otherParties.filter((party) => {
        const search = searchTerm.toLowerCase();
        const name = party.name?.toLowerCase() || "";
        const phone = party.phone?.toLowerCase() || "";
        return name.includes(search) || phone.includes(search);
    });

    const columnsWithAddressAndTax = [
        ...columns,
        {
            field: "address",
            headerName: "Address",
            width: 200,
        },
        {
            field: "taxNumber",
            headerName: "Tax Number",
            width: 150,
        },
    ];

    const customersCount = filteredOtherParties.filter((p) => p.type === "Customer").length;
    const companiesCount = filteredOtherParties.filter((p) => p.type === "Company").length;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #0097a7 0%, #006064 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <GroupIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("OTHER_PARTIES")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip
                                label={`${customersCount} ${t("CUSTOMER")}`}
                                size="small"
                                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600 }}
                            />
                            <Chip
                                label={`${companiesCount} ${t("COMPANY")}`}
                                size="small"
                                sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600 }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <TextField
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`${t("SEARCH")}...`}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.7)" }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "white",
                                borderRadius: 2,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.4)" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.7)" },
                                "& input::placeholder": { color: "rgba(255,255,255,0.6)" },
                                "& input": { color: "white" },
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddDialogOpen(true)}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1.5px solid rgba(255,255,255,0.4)",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                        }}
                    >
                        {t("ADD_OTHER_PARTY")}
                    </Button>
                </Box>
            </Paper>

            {/* Data Grid */}
            <CustomDataGrid
                columns={columnsWithAddressAndTax}
                rows={filteredOtherParties}
                actions={actions}
                perPage={20}
                hasEdit={false}
            />

            {/* Add Dialog */}
            {authHeader && isAddDialogOpen && (
                <AddOtherPartyDialog
                    isOpen={isAddDialogOpen}
                    onClose={() => {
                        setIsRefresh(!isRefresh);
                        setIsAddDialogOpen(false);
                    }}
                    authHeader={authHeader}
                />
            )}

            {/* Edit Dialog */}
            {authHeader && isEditDialogOpen && selectedEditId && (
                <EditOtherPartyDialog
                    isOpen={isEditDialogOpen}
                    onClose={() => {
                        setIsRefresh(!isRefresh);
                        setIsEditDialogOpen(false);
                        setSelectedEditId(null);
                    }}
                    authHeader={authHeader}
                    otherPartyId={selectedEditId}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this row?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomerList;
